import { memo, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis';
import { KPIWidget as KPIWidgetUI } from '@trinity/chat-components/components/QuantAnalysis/KPIWidget';
import { kindFor } from '@trinity/chat-components/components/Widgets/chart-kinds';
import { CompactChartLegend } from '@trinity/chat-components/components/Widgets/Charts/CompactLegend';
import { nivoOptionsFromChart, renderNivoChart } from '@trinity/chat-components/components/Widgets/nivo-chart';
import { ProgressBarWidget } from '@trinity/chat-components/components/Widgets/ProgressBarWidget';
import { SemiCircleProgressWidget } from '@trinity/chat-components/components/Widgets/SemiCircleProgressWidget';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';

function renderKPIWidget(chartData: QuickChartData & { type: 'kpi-widget' }) {
  return (
    <KPIWidgetUI
      title={chartData.title ?? ''}
      backgroundColor="transparent"
      values={chartData.values ?? []}
      labels={chartData.labels ?? []}
    />
  );
}

function renderProgressBar(chartData: QuickChartData & { type: 'progress-bar-widget' }, colors: string[]) {
  return (
    <ProgressBarWidget
      title={chartData.title ?? ''}
      backgroundColor="transparent"
      values={chartData.values ?? []}
      minValues={chartData.minValues ?? []}
      maxValues={chartData.maxValues ?? []}
      labels={chartData.labels ?? []}
      colors={chartData.colors ?? colors}
    />
  );
}

function renderSemiCircleProgress(chartData: QuickChartData & { type: 'semi-circle-progress-widget' }, colors: string[]) {
  return (
    <SemiCircleProgressWidget
      title={chartData.title ?? ''}
      backgroundColor="transparent"
      values={chartData.values ?? []}
      minValues={chartData.minValues ?? []}
      maxValues={chartData.maxValues ?? []}
      labels={chartData.labels ?? []}
      colors={chartData.colors ?? colors}
      units={chartData.units}
      insights={chartData.insights}
    />
  );
}

function renderDataTable(chartData: QuickChartData & { type: 'data-table' }, colors: string[]) {
  const tableData = {
    columnNames: chartData.customColumnNames ?? chartData.data.columnNames,
    rowNames: chartData.customRowNames ?? chartData.data.rowNames,
    records: chartData.data.records,
    metadata: chartData.data.metadata,
  };
  return (
    <DataTableContainer>
      <AnalysisTable
        data={tableData}
        headerBackgroundColor={chartData.headerColor ?? colors[0]}
        initialSortConfig={chartData.sortConfig}
        disableSorting={false}
        disableSelection={true}
      />
    </DataTableContainer>
  );
}



/**
 * ChartVisualizationUI — pure presentation component for chart widgets.
 *
 * All data is received via props. Uses `colors` prop instead of the
 * app-local `useOptionsColors` hook, so it is reusable across packages.
 */

type Props = {
  /** The chart data shape (discriminated union by chart type) */
  chartData: QuickChartData;
};

export const ChartVisualizationUI = memo(function ChartVisualizationUI({
  chartData,
}: Props): React.ReactElement | null {
  // Use colors embedded in the chart data (set by HTTP agents), falling back to the default palette.
  const chartDataColors = 'colors' in chartData ? (chartData as { colors?: string[] }).colors : undefined;
  const colors = chartDataColors ?? DEFAULT_COLORS;
  const [legendOpen, setLegendOpen] = useState(false);

  const legend = useMemo(() => kindFor(chartData.type).legend(chartData, colors), [chartData, colors]);

  // Estimate legend rows: ~4 items per row at typical widths, ~22px per row
  const legendRows = legend.length > 0 ? Math.ceil(legend.length / 4) : 0;
  const chartHeight = Math.max(180, 280 - legendRows * 24);

  return (
    <LegendCollapseToggleContext.Provider value={[legendOpen, setLegendOpen]}>
      <ChartContainer>
        <ChartContent style={{ height: chartHeight }}>{renderChart()}</ChartContent>
        {legend.length > 0 && <CompactChartLegend legend={legend} />}
      </ChartContainer>
    </LegendCollapseToggleContext.Provider>
  );

  function renderChart(): React.ReactElement | null {
    switch (chartData.type) {
      case 'bar':
      case 'stacked-bar':
      case 'pie':
      case 'multiple-clustered-bar':
      case 'multiple-stacked-bar':
      case 'line':
      case 'multiple-line':
        return renderNivoChart(chartData, colors, nivoOptionsFromChart(chartData));
      case 'kpi-widget':
        return renderKPIWidget(chartData);
      case 'progress-bar-widget':
        return renderProgressBar(chartData, colors);
      case 'semi-circle-progress-widget':
        return renderSemiCircleProgress(chartData, colors);
      case 'data-table':
        return renderDataTable(chartData, colors);
      default: {
        // A chart type added without a renderer is a type error, not a blank widget.
        const _exhaustive: never = chartData;
        return null;
      }
    }
  }
});

const ChartContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
});

const ChartContent = styled.div({
  minHeight: 0,
});

const DataTableContainer = styled.div({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});
